import { Element } from './types';

export interface ProjectPhase {
    id: string;
    name: string;
    sequenceNumber: number;
}

export interface PhaseFilter {
    id: string;
    name: string;
    rules: {
        phaseStatus: 'New' | 'Existing' | 'Demolished' | 'Temporary';
        display: 'ByCategory' | 'Overridden' | 'NotDisplayed';
        overrideColor?: string;
        halftone?: boolean;
    }[];
}

export interface ElementPhaseData {
    phaseCreated: string; // Phase ID
    phaseDemolished?: string; // Phase ID if demolished
}

export class PhasingManager {

    private phases: Map<string, ProjectPhase> = new Map();
    private filters: Map<string, PhaseFilter> = new Map();
    private elementPhases: Map<string, ElementPhaseData> = new Map();

    constructor() {
        // Default phases
        this.createPhase('Existing', 1);
        this.createPhase('New Construction', 2);
    }

    /**
     * Create a new project phase.
     */
    createPhase(name: string, sequenceNumber: number): ProjectPhase {
        const phase: ProjectPhase = {
            id: crypto.randomUUID(),
            name,
            sequenceNumber
        };
        this.phases.set(phase.id, phase);
        return phase;
    }

    /**
     * Get all phases sorted by sequence.
     */
    getPhases(): ProjectPhase[] {
        return Array.from(this.phases.values()).sort((a, b) => a.sequenceNumber - b.sequenceNumber);
    }

    /**
     * Create a phase filter.
     */
    createFilter(name: string, rules: PhaseFilter['rules']): PhaseFilter {
        const filter: PhaseFilter = {
            id: crypto.randomUUID(),
            name,
            rules
        };
        this.filters.set(filter.id, filter);
        return filter;
    }

    /**
     * Set phase data for an element.
     */
    setElementPhase(elementId: string, phaseCreated: string, phaseDemolished?: string) {
        this.elementPhases.set(elementId, { phaseCreated, phaseDemolished });
    }

    /**
     * Get element's phase status relative to a view phase.
     */
    getPhaseStatus(elementId: string, viewPhaseId: string): 'New' | 'Existing' | 'Demolished' | 'Future' | 'NotVisible' {
        const data = this.elementPhases.get(elementId);
        if (!data) return 'New'; // Default

        const viewPhase = this.phases.get(viewPhaseId);
        const createdPhase = this.phases.get(data.phaseCreated);
        const demolishedPhase = data.phaseDemolished ? this.phases.get(data.phaseDemolished) : null;

        if (!viewPhase || !createdPhase) return 'NotVisible';

        // Created after view phase = Future (not visible)
        if (createdPhase.sequenceNumber > viewPhase.sequenceNumber) {
            return 'Future';
        }

        // Demolished before or at view phase = Demolished
        if (demolishedPhase && demolishedPhase.sequenceNumber <= viewPhase.sequenceNumber) {
            return 'Demolished';
        }

        // Created at view phase = New
        if (createdPhase.sequenceNumber === viewPhase.sequenceNumber) {
            return 'New';
        }

        // Created before view phase (and not demolished) = Existing
        return 'Existing';
    }

    /**
     * Filter elements for a view based on phase and filter.
     */
    filterElements(elements: Element[], viewPhaseId: string, filterId?: string): { element: Element; status: string; override?: { color?: string; halftone?: boolean } }[] {
        const filter = filterId ? this.filters.get(filterId) : null;
        const result: { element: Element; status: string; override?: { color?: string; halftone?: boolean } }[] = [];

        elements.forEach(el => {
            const status = this.getPhaseStatus(el.id, viewPhaseId);

            if (status === 'Future' || status === 'NotVisible') {
                return; // Skip
            }

            let override: { color?: string; halftone?: boolean } | undefined;

            if (filter) {
                const rule = filter.rules.find(r => r.phaseStatus === status);
                if (rule) {
                    if (rule.display === 'NotDisplayed') return; // Skip
                    if (rule.display === 'Overridden') {
                        override = { color: rule.overrideColor, halftone: rule.halftone };
                    }
                }
            }

            result.push({ element: el, status, override });
        });

        return result;
    }
}
